/* eslint-disable react/require-default-props */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import Cookies from 'js-cookie';
// eslint-disable-next-line import/no-extraneous-dependencies
import { StatusCodes } from 'http-status-codes';
import { ERROR_MESSAGES } from '@common/constants';
import { languagesList } from '@common/language';
import { getErrorMessage, isOldToken } from '@common/utils';
import { InterviewType } from '@common/ts/enums';
import ArrowPathIcon from './src/components/icons/ArrowPathIcon';
import ChevronDoubleDownIcon from './src/components/icons/ChevronDoubleDownIcon';
import PlayIcon from './src/components/icons/PlayIcon';
import ChatInput from './src/components/input/ChatInput';
import TextMessage from './src/components/messages/text/TextMessage';
import { Button } from './src/components/shadcn/button';
import TooltipWrapper from './src/components/tooltip/TooltipWrapper';
import { cn } from './src/lib/utils';
import useChatStore from './src/store';
import 'react-toastify/dist/ReactToastify.css';
import { useVideoRecorder } from './src/hooks/useVideoRecording';
import useChatSettingsStore from './src/ChatSettingsStore';
import VideoPreview from './src/components/messages/video/VideoPreview';
import { ChatErrors } from './src/enums';
import { StandAloneChatProps, ProgressData } from './src/types';
import { usePermissionDialogs } from './src/hooks/usePermissionDialogs';
import { PermissionDialog } from './src/components/dialogs/PermissionDialog';
import { MediaTestDialog } from './src/components/dialogs/MediaTestDialog';
import {
  sendStatusChangeToParent,
  sendConversationCompleteToParent,
  sendDigestReadyToParent,
  ConversationStatus,
  type ConversationCompleteData,
  type DigestReadyData,
} from './src/utils/iframeUtils';
import { iframeLocalStorage } from './src/utils/iframeLocalStorage';

const axiosInstanceChat = axios.create({
  baseURL: import.meta.env.VITE_CHAT_URL,
});

function ChatPresentation({
  chatHeight,
  restartChatButton = false,
  handleRestartChat,
  isTestChat,
  isRefreshLoading,
  themeData,
  areScreeningQuestionsEnded,
  token,
  projectStorageSuffix,
  isProjectCompleted,
}: StandAloneChatProps) {
  const { chatSettingsData } = useChatSettingsStore();
  const { chat_id } = useParams();
  const [searchParams] = useSearchParams();
  const [languageCode, setLanguageCode] = useState<string>('en');

  // Get language from URL parameter or localStorage
  useEffect(() => {
    // First try to get from URL
    const urlLanguage = searchParams.get('language');
    if (urlLanguage) {
      const languageItem = languagesList.language.find(
        (lang) => lang.value.toLowerCase() === urlLanguage.toLowerCase(),
      );
      if (languageItem && languageItem.code) {
        setLanguageCode(languageItem.code);
        return;
      }
    }

    // Fallback to localStorage using the provided projectStorageSuffix
    if (projectStorageSuffix) {
      // Try to get language code from localStorage
      const storedLanguageCode = iframeLocalStorage.getItem(
        `current_project_language_code${projectStorageSuffix}`,
      );
      if (storedLanguageCode) {
        setLanguageCode(storedLanguageCode);
      }
    }
  }, [searchParams, projectStorageSuffix]);

  const {
    data: { messages },
    isLastMessageRerendered,
    currentConversationId,
  } = useChatStore((state) => state);
  const [showBackToBottom, setShowBackToBottom] = useState(false);
  const [isVoiceBoxOpen, setIsVoiceBoxOpen] = useState(false);
  const chatEnvironment = searchParams.get('environment');
  const isSandboxChat = chatEnvironment === 'sandbox';
  const chatBoxRef = useRef<HTMLDivElement>(null);
  const [progressData, setProgressData] = useState<ProgressData | null>(null);
  const previousStatusRef = useRef<string | null>(null); // TE-1799: Track previous status for change detection
  const navigate = useNavigate();
  const {
    previewStream,
    isVideoRecordingActive,
    showVideoRecordingModal,
    handleSubmitVideo,
    isVideoRecordingPressed,
    handleOpenVideoModal,
    isFrontCamera,
    videoPreviewUrl,
    handleStartVideoRecording,
    handleFlipCamera,
    statusVideo,
    handleStopVideoRecording,
    handlePauseVideoRecording,
    handleResumeVideoRecording,
    handleExitVideoRecording,
    capturing,
    setShowVoiceMainInput,
    handleCloseVideoModal,
    isVideoSending,
    isVideoStopping,
    isVideoPausing,
    isVideoExiting,
    isVideoStarting,
    isVideoResuming,
    isSwitchingCamera,
    getVideoConstraints,
  } = useVideoRecorder(projectStorageSuffix);

  // Store reference to voice recording trigger function
  const voiceRecordingTriggerRef = useRef<(() => void) | null>(null);

  // Handler for after media test completion
  const handleMediaTestComplete = (action: InterviewType.AUDIO | InterviewType.VIDEO) => {
    if (action === InterviewType.VIDEO) {
      handleOpenVideoModal();
    } else if (action === InterviewType.AUDIO) {
      // Trigger voice recording if the function is available
      if (voiceRecordingTriggerRef.current) {
        voiceRecordingTriggerRef.current();
      }
    }
  };

  // Callback to receive voice recording trigger function from ChatInput
  const handleVoiceRecordingTrigger = (startRecordingFn: () => void) => {
    voiceRecordingTriggerRef.current = startRecordingFn;
  };

  // Permission dialogs logic
  const {
    permissions,
    videoEnabled,
    audioEnabled,
    showPermissionModal,
    needsPermissionDialog,
    requestedPermissions,
    handleGrantAccess,
    handleCloseModal,
    requestCameraPermission,
    requestMicrophonePermission,
    mediaTest,
    isFirefoxAndroid,
    isGrantingAccess,
  } = usePermissionDialogs({
    chatSettingsData,
    isTestChat,
    onMediaTestComplete: handleMediaTestComplete,
  });

  const fetchChatProgress = async () => {
    try {
      if (currentConversationId || areScreeningQuestionsEnded) {
        const response = await axiosInstanceChat.get(`/chat/progress/${currentConversationId}`);
        setProgressData(response.data);
      }
    } catch (err) {
      const axiosError = err as AxiosError;
      const chatError = axiosError?.response?.status;
      const message = getErrorMessage(axiosError);

      // Chat not found because conversation does not exist
      const noConversationFound =
        chatError === StatusCodes.NOT_FOUND && message === ERROR_MESSAGES.CONVERSATION_NOT_FOUND;

      // This needs to be checked
      if (chatError === ChatErrors.CHAT_EXPIRED) {
        navigate(`/chat/error?error_status=${ChatErrors.CHAT_EXPIRED}`);
      }

      if (
        [ChatErrors.TEST_CONVERSATION_NOT_FOUND, ChatErrors.UNAUTHORIZED].includes(chatError!) ||
        noConversationFound
      ) {
        iframeLocalStorage.clear();
        window.location.reload();
      }
      console.error(err);
    }
  };

  // TE-1799: Fetch complete iframe data for conversation complete event
  const fetchIframeData = async (): Promise<ConversationCompleteData | null> => {
    try {
      if (!currentConversationId) return null;
      const response = await axiosInstanceChat.get(`/chat/iframe-data/${currentConversationId}`);
      return response.data;
    } catch (err) {
      console.error('Failed to fetch iframe data:', err);
      return null;
    }
  };

  const handleBackToBottom = () => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight + 64;
    }
  };

  // show scroll to bottom button
  const handleScroll = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    if (
      e.currentTarget.scrollHeight - e.currentTarget.scrollTop - e.currentTarget.clientHeight <
      350
    ) {
      setShowBackToBottom(false);
    } else {
      setShowBackToBottom(true);
    }
  };

  useLayoutEffect(() => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [messages, isLastMessageRerendered]);

  useEffect(() => {
    handleBackToBottom();
  }, [isVoiceBoxOpen]);

  useEffect(() => {
    fetchChatProgress();
    if (!token) return;
    const isOldJwtToken = isOldToken(token);
    if (isOldJwtToken) Cookies.remove('token');
  }, [messages]);

  const progressCount: number | undefined = progressData?.count;
  const progressLimit: number | undefined = progressData?.limit;
  const isProgressDone: boolean | undefined = progressData?.done;
  const percentage = progressCount && progressLimit ? (progressCount / progressLimit) * 100 : 0;

  // TE-1799: Keep polling after conversation is done to detect digest status
  // Poll every 3 seconds for up to 90 seconds after conversation is done
  // to detect when status changes from 'done' to 'digested'
  useEffect(() => {
    // Only start polling when conversation is done but not yet digested
    if (!isProgressDone || progressData?.status === ConversationStatus.DIGESTED) {
      return;
    }

    let pollCount = 0;
    const maxPolls = 30; // 30 polls * 3 seconds = 90 seconds

    const pollInterval = setInterval(() => {
      pollCount += 1;

      // Stop polling after 90 seconds (30 iterations)
      if (pollCount >= maxPolls) {
        clearInterval(pollInterval);
        return;
      }

      fetchChatProgress();
    }, 3000);

    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(pollInterval);
    };
  }, [isProgressDone, progressData?.status]);

  // TE-1799: Enhanced iframe messaging - status change detection and event sending
  useEffect(() => {
    const handleStatusChange = async () => {
      const currentStatus = progressData?.status;
      const previousStatus = previousStatusRef.current;

      // If status hasn't changed, do nothing
      if (currentStatus === previousStatus) return;

      // Update previous status reference
      previousStatusRef.current = currentStatus || null;

      // If we have a new status, send STATUS_CHANGE event
      if (currentStatus) {
        sendStatusChangeToParent({
          status: currentStatus as ConversationStatus,
          conversationId: currentConversationId || '',
          timestamp: new Date().toISOString(),
          progress: {
            count: progressData?.count || 0,
            limit: progressData?.limit || 0,
            done: progressData?.done || false,
          },
        });
      }

      // Handle completion statuses - send CONVERSATION_COMPLETE
      // This includes DONE, SANDBOX_DONE, and DIGESTED
      const completionStatuses = [
        ConversationStatus.DONE,
        ConversationStatus.SANDBOX_DONE,
        ConversationStatus.DIGESTED,
      ];

      if (completionStatuses.includes(currentStatus as ConversationStatus)) {
        // Only send CONVERSATION_COMPLETE if we're transitioning to a completion status
        // (not if we're already at a completion status from a previous check)
        const wasAlreadyComplete = previousStatus
          ? completionStatuses.includes(previousStatus as ConversationStatus)
          : false;

        if (!wasAlreadyComplete) {
          const iframeData = await fetchIframeData();
          if (iframeData) {
            sendConversationCompleteToParent(iframeData);
          }
        }
      }

      // Handle DIGESTED status - send DIGEST_READY
      if (
        currentStatus === ConversationStatus.DIGESTED &&
        previousStatus !== ConversationStatus.DIGESTED
      ) {
        const iframeData = await fetchIframeData();
        if (iframeData?.digest) {
          const digestData: DigestReadyData = {
            conversationId: iframeData.conversationId,
            summary: iframeData.digest.summary,
            themes: iframeData.digest.themes,
            opportunities: iframeData.digest.opportunities,
            quotes: iframeData.digest.quotes,
          };
          sendDigestReadyToParent(digestData);
        }
      }

      // Legacy cleanup when progress is done
      if (isProgressDone) {
        const localStorageKeysToRemove = [
          `current_project_language_code${projectStorageSuffix}`,
          `current_project_language${projectStorageSuffix}`,
          `chat_settings/${chat_id}`,
          `screening_state${projectStorageSuffix}`,
        ];
        localStorageKeysToRemove.forEach((key) => {
          iframeLocalStorage.removeItem(key);
        });
      }
    };

    handleStatusChange();
  }, [progressData, isProgressDone, currentConversationId, chat_id, projectStorageSuffix]);

  const isTextRightToLeft = ['ar', 'iw', 'fa', 'ur'].includes(languageCode);
  const lastAssistantMessage =
    messages && messages.length > 0 ? messages[messages.length - 1] : null;

  return (
    <div className="flex relative flex-col items-center w-full px-2 max-w-[1000px] mx-auto min500:px-0">
      {/* chat ui */}
      <div className={cn('relative w-full h-full')}>
        {/* flag */}
        {!isTestChat && isSandboxChat && (
          <div
            style={{
              backgroundColor: themeData?.['chat-primary'] || '#FF6F98',
            }}
            className="absolute left-0 right-0 z-50 flex items-center justify-center w-full text-xs font-medium text-white shadow-md min500:rounded-t-none min500:top-0 h-7 rounded-t-xl top-3 bg-chat-primary"
          >
            Test chat
          </div>
        )}

        {/* progress bar */}
        <div
          style={{
            backgroundColor: themeData?.['chat-progress-bar-bg'] || '#FF6F98',
          }}
          className={cn(
            'absolute left-0 right-0 min500:top-7 border-t-2 border-white overflow-hidden z-50 w-full h-6 top-10',
            !isSandboxChat && 'top-3 min500:rounded-t-none rounded-t-lg min500:top-0 border-none',
          )}
        >
          <div
            style={{
              width: `${percentage ?? 0}%`,
              backgroundColor: themeData?.primary || '#096846',
            }}
            className={cn(
              'flex w-full  justify-center items-center h-6 transition-all duration-700 ease-in-out rounded-r-full',
              percentage === 100 && 'rounded-r-none',
            )}
          ></div>
        </div>
        {/* restart test chat */}
        {restartChatButton && (
          <TooltipWrapper text="Start conversation from the start." className="">
            <Button
              style={{ backgroundColor: themeData?.primary || '#096846' }}
              onClick={handleRestartChat}
              className="absolute z-50 transition-all border-2 border-white shadow-xl top-11 right-4"
              disabled={isProjectCompleted || isRefreshLoading}
            >
              {messages?.length === 0 ? (
                <PlayIcon className="size-5" />
              ) : (
                <ArrowPathIcon className={cn('size-5', isRefreshLoading && 'animate-spin')} />
              )}
            </Button>
          </TooltipWrapper>
        )}

        {/* restart sandbox chat */}
        {isSandboxChat && (
          <TooltipWrapper text="Start conversation from the start." className="">
            <Button
              style={{ backgroundColor: themeData?.primary || '#096846' }}
              onClick={handleRestartChat}
              className="absolute z-10 border-2 border-white shadow-xl top-[70px] right-4 transition-all"
            >
              {messages?.length === 0 ? (
                <PlayIcon className="size-5" />
              ) : (
                <ArrowPathIcon className="size-5" />
              )}
            </Button>
          </TooltipWrapper>
        )}
        <div
          ref={chatBoxRef}
          onScroll={handleScroll}
          className={`w-full mt-3 min500:mt-0 min500:rounded-none min500:border-none ${isVoiceBoxOpen && 'pb-[33vh]'} scroll-smooth relative min500:border-none min500:shadow-none border-2 rounded-xl shadow overflow-hidden overflow-y-auto
          ${isTestChat ? 'bg-gray-50' : 'bg-white'}
          ${chatHeight || ''}`}
        >
          <div className="w-full">
            {/* chat */}
            <div
              className={cn(
                'flex flex-col w-full h-full px-3 md700:px-1.5',
                isTestChat ? 'bg-gray-50' : 'bg-white',
              )}
            >
              {/* chat boxes */}
              <div className="flex flex-col items-center w-full h-full px-2">
                {/* messages */}
                <div
                  className={cn(
                    'flex flex-col w-full gap-5 mt-8 mb-3 h-fit',
                    isSandboxChat && 'mt-16',
                  )}
                >
                  {messages &&
                    messages.map((item, index) => (
                      <TextMessage
                        isTestChat={isTestChat}
                        key={item._id}
                        {...item}
                        isLastMessage={index === messages.length - 1}
                      />
                    ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/*  back to bottom */}
      {showBackToBottom && (
        <div className="relative w-full">
          <button
            onClick={handleBackToBottom}
            type="button"
            style={{ backgroundColor: themeData?.primary || '#096846' }}
            className="absolute right-0.5 bottom-[calc(100%+100px)] rounded-l-xl text-white w-10 h-10 flex items-center justify-center"
          >
            <ChevronDoubleDownIcon className="size-5" />
          </button>
        </div>
      )}

      {showVideoRecordingModal && chatSettingsData?.includes('video') && (
        <VideoPreview
          handleSubmitVideo={handleSubmitVideo}
          stream={previewStream}
          themeData={themeData}
          isTestChat={isTestChat}
          isFrontCamera={isFrontCamera}
          videoPreviewUrl={videoPreviewUrl}
          handleStartVideoRecording={handleStartVideoRecording}
          handleFlipCamera={handleFlipCamera}
          statusVideo={statusVideo}
          handleStopVideoRecording={handleStopVideoRecording}
          handlePauseVideoRecording={handlePauseVideoRecording}
          handleResumeVideoRecording={handleResumeVideoRecording}
          handleExitVideoRecording={handleExitVideoRecording}
          capturing={capturing}
          previewStream={previewStream}
          handleCloseVideoModal={handleCloseVideoModal}
          setShowVoiceMainInput={setShowVoiceMainInput}
          isVideoSending={isVideoSending}
          isVideoStopping={isVideoStopping}
          isVideoPausing={isVideoPausing}
          isVideoExiting={isVideoExiting}
          isVideoStarting={isVideoStarting}
          isVideoResuming={isVideoResuming}
          isSwitchingCamera={isSwitchingCamera}
          getVideoConstraints={getVideoConstraints}
          languageCode={languageCode}
          message={{
            text: lastAssistantMessage?.text,
            hasMedia: Object.keys(lastAssistantMessage?.data || {}).length > 0,
          }}
        />
      )}

      {!isProgressDone && (
        <div className="flex items-center w-full mb-2 md600:flex-col">
          <div className={cn('w-full', isTestChat && 'mt-4')}>
            <ChatInput
              isTestChat={isTestChat}
              setIsVoiceBoxOpen={setIsVoiceBoxOpen}
              isVoiceBoxOpen={isVoiceBoxOpen}
              themeData={themeData}
              isVideoRecordingActive={isVideoRecordingActive}
              isVideoRecordingPressed={isVideoRecordingPressed}
              handleOpenVideoModal={handleOpenVideoModal}
              requestCameraPermission={requestCameraPermission}
              requestMicrophonePermission={requestMicrophonePermission}
              permissions={permissions}
              languageCode={languageCode}
              onVoiceRecordingTrigger={handleVoiceRecordingTrigger}
              projectStorageSuffix={projectStorageSuffix}
            />
          </div>
        </div>
      )}

      <div
        className={`w-full min450:px-2 px-0 pointer-events-none opacity-0 transition-opacity duration-300 ease-in mt-5 ${isProgressDone && 'opacity-100'}  `}
      >
        <p
          className={cn(
            'text-md font-medium bg-white border-2 rounded-full shadow h-12 flex items-center justify-center',
          )}
        >
          {progressData?.completedMessage || 'Completed 🥳'}
        </p>
      </div>

      {/* Media Permissions Dialog */}
      <PermissionDialog
        isOpen={showPermissionModal}
        onClose={handleCloseModal}
        onGrantAccess={handleGrantAccess}
        requestedPermissions={requestedPermissions}
        permissions={permissions}
        themeData={themeData}
        needsPermissionDialog={needsPermissionDialog}
        videoEnabled={videoEnabled}
        audioEnabled={audioEnabled}
        isFirefoxAndroid={isFirefoxAndroid}
        languageCode={languageCode}
        isGrantingAccess={isGrantingAccess}
        isTextRightToLeft={isTextRightToLeft}
      />

      {/* Media Test Dialog */}
      <MediaTestDialog
        isOpen={mediaTest.showMediaTestModal}
        onClose={mediaTest.closeMediaTest}
        onContinue={mediaTest.handleTestComplete}
        testPermissions={mediaTest.testPermissions}
        themeData={themeData}
        languageCode={languageCode}
        isTextRightToLeft={isTextRightToLeft}
      />
    </div>
  );
}

export default ChatPresentation;
